// Bascule light/dark/auto avec fondu (View Transition API).

const THEME_PICKER_SELECTOR = 'input[data-theme-picker]'

export function resolveTheme(theme) {
  if (theme === "auto") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }
  return theme
}

// Un skin du mode fun impose le thème sur lequel il est dessiné (cf. FunModeHelper::FUN_SKINS) :
// tant qu'il est porté, il gagne sur la préférence — celle-ci reste enregistrée et cochée, elle
// reprend la main quand on enlève le skin.
function skinTheme() {
  const theme = document.body?.dataset.funSkinTheme
  return theme === "light" || theme === "dark" ? theme : null
}

export function syncThemePickers(theme) {
  document.querySelectorAll(THEME_PICKER_SELECTOR).forEach((input) => {
    input.checked = input.value === theme
  })

  document.querySelectorAll('input[data-theme-value]').forEach((input) => {
    input.value = theme
  })
}

// Pose un thème déjà résolu, sans fondu. `applyTheme` l'enrobe de sa View Transition ; les
// skins du mode fun l'appellent dans la leur (cf. utils/fun_settings.js).
export function commitTheme(resolved) {
  const root = document.documentElement
  const current =
    document.body.getAttribute("data-bs-theme") ||
    root.getAttribute("data-bs-theme")

  document.body.setAttribute("data-bs-theme", resolved)
  root.setAttribute("data-bs-theme", resolved)

  if (current !== resolved) {
    document.dispatchEvent(
      new CustomEvent("depozen:theme-changed", { detail: { theme: resolved } })
    )
  }
}

export function applyTheme(theme) {
  if (!["light", "dark", "auto"].includes(theme)) return

  if (document.body) {
    document.body.dataset.themePreference = theme
  }

  const resolved = skinTheme() || resolveTheme(theme)
  const root = document.documentElement
  const current =
    document.body.getAttribute("data-bs-theme") ||
    root.getAttribute("data-bs-theme")

  syncThemePickers(theme)

  if (current === resolved) return

  const apply = () => commitTheme(resolved)

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  if (typeof document.startViewTransition === "function" && !reduceMotion) {
    root.classList.add("theme-transitioning")
    const transition = document.startViewTransition(apply)
    transition.finished.finally(() => {
      root.classList.remove("theme-transitioning")
    })
  } else {
    apply()
  }
}

function persistResolvedTheme(theme) {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
  if (!token || !["light", "dark"].includes(theme)) return

  fetch("/set_theme", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": token
    },
    body: JSON.stringify({ theme })
  }).catch(() => {})
}

// Préférence « système » (`data-theme-preference="auto"` sur le body) : applique prefers-color-scheme.
export function syncThemeFromOs() {
  if (document.body?.dataset.themePreference !== "auto") return

  const resolved = resolveTheme("auto")
  applyTheme("auto")
  persistResolvedTheme(resolved)
}

let osThemeObserverStarted = false

// Écoute globale des changements OS (macOS / Windows) — une seule fois par onglet.
export function startOsThemeObserver() {
  if (osThemeObserverStarted) return
  osThemeObserverStarted = true

  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => syncThemeFromOs())

  document.addEventListener("turbo:load", () => syncThemeFromOs())
  syncThemeFromOs()
}
